import { faLayerPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BlackbaudRaisersEdgeIntegration as BREIntegration,
  IBlackbaudContactMapping,
  TBlackbaudContactMappingKey,
} from '@feathr/blackbox';
import { EBREContactMappings } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { Button, ModalV1, Select, toast } from '@feathr/components';
import { RaisersEdgeContactLabelMap } from '@feathr/extender/styles/blackbaud_raisers_edge';
import { enumKeys, useToggle } from '@feathr/hooks';

interface IProps {
  integration: BREIntegration;
}

interface IContactMappingOption {
  id: TBlackbaudContactMappingKey;
  name: string;
}

function BlackbaudAddMapping({ integration }: IProps): JSX.Element {
  const { t } = useTranslation();
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const [selectedFields, setSelectedFields] = useState<TBlackbaudContactMappingKey[]>([]);
  const [contactMappings, setContactMappings] = useState<IBlackbaudContactMapping[] | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    async function getMappings(): Promise<void> {
      try {
        // TODO: Refactor to use ContactMappings.list() as part of #2651.
        setIsLoading(true);
        const mappings = await integration.getContactMappings();
        setIsLoading(false);
        setContactMappings(mappings);
      } catch (error) {
        setIsLoading(false);
        toast(t("Couldn't load mappings. {{error}}", { error }), { type: 'error' });
      }
    }

    if (!contactMappings) {
      getMappings();
    }
  }, []);

  const contactMappingOptions: IContactMappingOption[] = enumKeys(EBREContactMappings).map(
    (key) => ({
      id: key,
      name: RaisersEdgeContactLabelMap(t, key),
    }),
  );

  // Filter out the contact mapping options that are already mapped
  const filteredContactMappingOptions = contactMappingOptions.filter((option) => {
    return !contactMappings?.some(
      (mapping: IBlackbaudContactMapping): boolean => mapping.key === option.id,
    );
  });

  const isDisabled = filteredContactMappingOptions.length === 0;

  function handleOnClick(): void {
    toggleIsModalOpen();
  }

  async function handleConfirm(): Promise<void> {
    toggleIsModalOpen();
    try {
      await integration.addContactMappings(selectedFields);
      toast(t('Added mapping.'), {
        type: 'success',
      });

      // TODO: Hard reloading will not be necesary with the implemention #2651.
      window.location.reload();
    } catch (error) {
      toast(t("Couldn't add mapping. Try again."), {
        type: 'error',
      });
    }
  }

  function handleSelectFields(options: ISelectOption[]): void {
    // Convert options to an array of option.id
    const selectedOptions: TBlackbaudContactMappingKey[] = options.map(
      (option) => option.id as TBlackbaudContactMappingKey,
    );
    setSelectedFields(selectedOptions);
  }

  return (
    <>
      <Button
        disabled={isDisabled || isLoading}
        onClick={handleOnClick}
        prefix={<FontAwesomeIcon icon={faLayerPlus} />}
        tooltip={
          isDisabled ? t("All Blackbaud Raiser's Edge contact fields have been added.") : undefined
        }
        tooltipPosition={'top-start'}
        type={'primary'}
      >
        {t('Add mapping')}
      </Button>
      {isModalOpen && (
        <ModalV1
          confirmButtonText={'Add'}
          confirmButtonType={'primary'}
          confirmDisabled={selectedFields.length === 0}
          controlled={true}
          onClose={toggleIsModalOpen}
          onConfirm={handleConfirm}
          t={t}
          title={t('Add Mapping')}
        >
          <p>
            {t(
              "Select additional Blackbaud Raiser's Edge contact fields to create mappings to Feathr fields.",
            )}
          </p>
          <Select
            isMulti={true}
            label={t("Blackbaud Raiser's Edge contact fields")}
            name={'blackbaud-raisers-edge-contact-fields'}
            onSelectMulti={handleSelectFields}
            options={filteredContactMappingOptions}
          />
        </ModalV1>
      )}
    </>
  );
}

export default observer(BlackbaudAddMapping);
